'use client'

import type { ComponentProps, ReactNode } from 'react'
import {
  CheckIcon,
  CopyIcon,
  EllipsisIcon,
  Loader2Icon,
  PauseIcon,
  PlayIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Volume2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { ghostButton, iconSwap, iconSwapIn, iconSwapOut } from './surfaces'

export type Reaction = 'up' | 'down' | null

/** Hover tooltip for an action button; renders bare children on touch devices. */
function ActionTip({ label, children }: { label: string; children: ReactNode }) {
  const isMobile = useIsMobile()
  if (isMobile) return <>{children}</>
  return (
    <TooltipProvider delayDuration={500}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export interface MessageActionsProps extends Omit<ComponentProps<'div'>, 'children'> {
  /** Read-aloud button state; omit to hide the button. */
  tts?: { state: 'idle' | 'loading' | 'playing' | 'paused'; onToggle: () => void }
  copied: boolean
  reaction: Reaction
  onCopy: () => void
  onReactionChange: (reaction: Reaction) => void
  onMore: () => void
}

export function MessageActions({
  tts,
  copied,
  reaction,
  onCopy,
  onReactionChange,
  onMore,
  className,
  ...props
}: MessageActionsProps) {
  const { t } = useTranslation('chat')
  const buttonClassName = cn(ghostButton, 'size-7')
  const ttsLabel = tts
    ? tts.state === 'playing'
      ? t('actions.pausePlayback')
      : tts.state === 'paused'
        ? t('actions.resumePlayback')
        : t('actions.play')
    : ''

  return (
    <div
      data-slot="message-actions"
      className={cn('flex items-center gap-1', className)}

      {...props}
    >
      {tts && (
        <ActionTip label={ttsLabel}>
          <button
            type="button"
            aria-label={ttsLabel}
            onClick={tts.onToggle}
            className={cn(
              buttonClassName,
              tts.state !== 'idle' &&
                'text-foreground/90 bg-foreground/[0.06] dark:bg-foreground/[0.09]'
            )}
          >
            {tts.state === 'loading' ? (
              <Loader2Icon className="size-3.5 animate-spin motion-reduce:animate-none" />
            ) : tts.state === 'playing' ? (
              <PauseIcon className="size-3.5 fill-current" />
            ) : tts.state === 'paused' ? (
              <PlayIcon className="size-3.5 fill-current" />
            ) : (
              <Volume2Icon className="size-3.5" />
            )}
          </button>
        </ActionTip>
      )}
      <ActionTip label={copied ? t('actions.copied') : t('actions.copy')}>
        <button
          type="button"
          aria-label={copied ? t('actions.copied') : t('actions.copy')}
          onClick={onCopy}
          className={cn(buttonClassName, 'grid place-items-center', copied && 'text-emerald-500')}
        >
          <CopyIcon className={cn(iconSwap, 'size-3.5', copied ? iconSwapOut : iconSwapIn)} />
          <CheckIcon className={cn(iconSwap, 'size-3.5', copied ? iconSwapIn : iconSwapOut)} />
        </button>
      </ActionTip>
      <ActionTip label={t('actions.helpful')}>
        <button
          type="button"
          aria-label={t('actions.helpful')}
          aria-pressed={reaction === 'up'}
          onClick={() => onReactionChange(reaction === 'up' ? null : 'up')}
          className={cn(
            buttonClassName,
            reaction === 'up' && 'bg-foreground/[0.06] text-foreground/90 dark:bg-foreground/[0.09]'
          )}
        >
          <ThumbsUpIcon className="size-3.5" />
        </button>
      </ActionTip>
      <ActionTip label={t('actions.unhelpful')}>
        <button
          type="button"
          aria-label={t('actions.unhelpful')}
          aria-pressed={reaction === 'down'}
          onClick={() => onReactionChange(reaction === 'down' ? null : 'down')}
          className={cn(
            buttonClassName,
            reaction === 'down' &&
              'bg-foreground/[0.06] text-foreground/90 dark:bg-foreground/[0.09]'
          )}
        >
          <ThumbsDownIcon className="size-3.5" />
        </button>
      </ActionTip>
      <button
        type="button"
        aria-label={t('actions.more')}
        onClick={onMore}
        className={buttonClassName}
      >
        <EllipsisIcon className="size-3.5" />
      </button>
    </div>
  )
}
